import React from "react";
import AuditForm from "@/components/AuditForm";
import { useAwakeMember } from "@/lib/awakeContext";
import { ArrowRight } from "lucide-react";

const MARK_URL =
  "https://customer-assets.emergentagent.com/job_founder-brand-audit/artifacts/l3rrkxb3_The_Weave_Logo_Symbol_only_Transparent_resize_50.png";

const WEAVE_COMMUNITY_URL = "https://www.wearetheweave.co.uk/community-choice";
const WEAVE_JOIN_URL = "https://awake.wearetheweave.co.uk/join";

export default function Landing() {
  const { isMember, memberEmail, dashboardUrl } = useAwakeMember();
  return (
    <div data-testid="landing-page">
      {/* HERO — deep teal block */}
      <section className="block-teal relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.07] pointer-events-none" />
        {/* large faded mark behind */}
        <img
          src={MARK_URL}
          aria-hidden="true"
          alt=""
          className="hidden lg:block absolute -right-16 -bottom-12 w-[640px] opacity-15 pointer-events-none select-none"
        />
        <div className="section-pad pt-12 md:pt-20 pb-20 md:pb-28 relative">
          <div className="overline mb-6 md:mb-8 rise text-center md:text-left" data-testid="hero-overline">
            The Weave · Founder Brand Audit
          </div>
          <h1
            data-testid="hero-title"
            className="font-display text-[40px] sm:text-6xl lg:text-[100px] leading-[1.02] sm:leading-[0.95] tracking-tight max-w-5xl rise text-center md:text-left mx-auto md:mx-0"
          >
            You don't have to
            <br />
            build alone.
            <br />
            Let's read your LinkedIn
            <br />
            <span className="squiggle">together.</span>
          </h1>
          <p
            className="mt-8 md:mt-10 text-base sm:text-lg md:text-xl max-w-2xl mx-auto md:mx-0 text-center md:text-left rise"
            style={{ animationDelay: "120ms", color: "rgba(251,247,240,0.85)" }}
          >
            Founder First. Business Second. We grade your LinkedIn against an 11-category audit,
            write a strategic overview of what you're really communicating, and hand you blog
            titles, sample posts and a tone-of-voice profile to grow on. Practical support for
            ambitious people.
          </p>
          <div
            className="mt-8 md:mt-10 flex flex-wrap justify-center md:justify-start items-center gap-3 rise"
            style={{ animationDelay: "220ms" }}
          >
            <a
              href="#audit-form"
              data-testid="hero-cta-audit"
              className="btn-red rounded-full h-12 md:h-14 px-6 md:px-7 inline-flex items-center gap-2 text-base md:text-lg font-medium"
            >
              {isMember ? "Run my audit" : "Complete audit"} <ArrowRight size={18} />
            </a>
            {isMember ? (
              <a
                href={dashboardUrl}
                data-testid="hero-cta-dashboard"
                className="rounded-full h-12 md:h-14 px-6 md:px-7 inline-flex items-center gap-2 text-base md:text-lg font-medium border-2"
                style={{
                  borderColor: "rgba(251,247,240,0.6)",
                  color: "var(--cream)",
                }}
              >
                ← Back to your dashboard
              </a>
            ) : (
              <>
                <a
                  href={WEAVE_COMMUNITY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-find-out-more"
                  className="rounded-full h-12 md:h-14 px-6 md:px-7 inline-flex items-center gap-2 text-base md:text-lg font-medium border-2"
                  style={{
                    borderColor: "rgba(251,247,240,0.6)",
                    color: "var(--cream)",
                  }}
                >
                  Find out more
                </a>
                <a
                  href={WEAVE_JOIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-join"
                  className="btn-yellow rounded-full h-12 md:h-14 px-6 md:px-7 inline-flex items-center gap-2 text-base md:text-lg font-medium"
                >
                  Join now
                </a>
              </>
            )}
          </div>

          {isMember && memberEmail && (
            <div
              className="mt-6 inline-flex items-center gap-2 text-sm rise"
              style={{ animationDelay: "320ms", color: "rgba(251,247,240,0.85)" }}
              data-testid="member-welcome-banner"
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: "var(--yellow)" }}
              />
              <span>
                Welcome back, <strong>{memberEmail}</strong>. Since you're already inside the room,
                this audit will attach itself to your member dashboard.
              </span>
            </div>
          )}

          {/* Sample score card on cream */}
          <div className="mt-12 md:mt-16 grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-6 lg:col-span-5 mx-auto md:mx-0 w-full max-w-md md:max-w-none">
              <div className="card-cream rounded-sm p-5 md:p-6">
                <div
                  className="overline"
                  style={{ color: "var(--muted)" }}
                >
                  Sample · Founder score
                </div>
                <div className="font-display text-6xl md:text-7xl mt-2 text-[var(--ink)]">
                  72<span className="text-2xl text-[var(--muted)]">/100</span>
                </div>
                <div className="hairline my-4" />
                <div className="space-y-2 text-sm">
                  {[
                    ["Headline", 6, "var(--teal)"],
                    ["About / Summary", 7, "var(--teal)"],
                    ["Engagement", 5, "var(--yellow-deep)"],
                    ["Authenticity", 8, "var(--red)"],
                  ].map(([k, v, color]) => (
                    <div key={k} className="flex items-center gap-3">
                      <div className="w-28 sm:w-32 text-[var(--muted)] text-xs sm:text-sm">{k}</div>
                      <div className="flex-1 bar-track">
                        <div
                          className="bar-fill"
                          style={{ width: `${v * 10}%`, background: color }}
                        />
                      </div>
                      <div className="w-8 text-right font-mono-mark text-xs">{v}/10</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-7 hidden md:block">
              <p className="font-display text-2xl md:text-3xl tracking-tight max-w-md leading-tight">
                What a real audit looks like — calm, specific, and useful.
                <br />
                <span style={{ color: "var(--yellow)" }}>No hype. No checklists.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <section className="border-y border-[var(--line)] bg-[var(--bg-2)] py-4 overflow-hidden">
        <div className="ticker-track flex gap-12 whitespace-nowrap font-mono-mark text-xs uppercase tracking-[0.2em] text-[var(--ink-2)]">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex gap-12">
              {[
                "Profile Picture",
                "Headline",
                "About / Summary",
                "Experience",
                "Skills",
                "Recommendations",
                "Education",
                "Engagement",
                "Connections",
                "URL & Contact",
                "Authenticity",
                "/ 11 categories audited",
              ].map((t, j) => (
                <span
                  key={`${i}-${t}-${j}`}
                  className={
                    j % 3 === 0
                      ? "text-[var(--teal)]"
                      : j % 3 === 1
                      ? "text-[var(--yellow-deep)]"
                      : "text-[var(--red)]"
                  }
                >
                  · {t}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="section-pad py-24">
        <div className="overline mb-3">[02] The process</div>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight max-w-4xl">
          Three steps. <span className="text-[var(--teal)]">No theatre.</span>
          <br />
          Just a thoughtful read of your profile.
        </h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            {
              n: "01",
              c: "var(--teal)",
              t: "Bring your profile",
              d: "Your LinkedIn URL plus the raw text of your headline, About, and a few recent posts. The more you share, the more useful the read.",
            },
            {
              n: "02",
              c: "var(--yellow-deep)",
              t: "We read it carefully",
              d: "Claude Sonnet 4.5, prompted in The Weave's voice, grades 11 categories from the founder branding rubric and writes a strategic narrative — not a checklist.",
            },
            {
              n: "03",
              c: "var(--red)",
              t: "Take a real next step",
              d: "Score, suggestions, content pillars, 10 blog titles, sample posts, and a tone-of-voice profile. Save your audit with Google when you're ready.",
            },
          ].map((s, i) => (
            <div key={s.n} className="rise" style={{ animationDelay: `${i * 80}ms` }}>
              <div className="font-display text-7xl" style={{ color: s.c }}>
                {s.n}
              </div>
              <h3 className="font-display text-2xl mt-4 tracking-tight">{s.t}</h3>
              <p className="mt-3 text-[var(--ink-2)] max-w-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FRAMEWORK */}
      <section
        id="framework"
        className="section-pad py-24 bg-[var(--bg-2)] border-y border-[var(--line)]"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <div className="overline mb-3">[03] The audit</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">
              The 11 categories <br />
              <span className="text-[var(--red)]">we read against.</span>
            </h2>
            <p className="mt-6 text-[var(--ink-2)] max-w-md">
              Adapted from The Weave's founder branding framework — used quietly with hundreds of
              ambitious operators. Each category gets a 0–10 score, a one-paragraph read, and
              concrete, doable fixes. No fluff. No hype.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7">
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
              {[
                "Profile Picture",
                "Headline",
                "About / Summary",
                "Experience",
                "Skills & Endorsements",
                "Recommendations",
                "Education",
                "Engagement",
                "Connections",
                "URL & Contact Info",
                "Overall Authenticity",
              ].map((t, i) => (
                <li
                  key={t}
                  className="flex items-center justify-between gap-4 py-4 border-b border-[var(--line)]"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-mono-mark text-xs text-[var(--muted)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display text-xl tracking-tight">{t}</span>
                  </div>
                  <span className="font-mono-mark text-xs text-[var(--muted)]">/10</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY pills */}
      <section className="section-pad py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <div className="overline mb-3">[04] What you'll feel</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">
              Calm confidence. <br />
              <span className="text-[var(--yellow-deep)]">Not hype.</span>
            </h2>
            <p className="mt-6 text-[var(--ink-2)] max-w-md">
              The Weave is what we believe a founder community should feel like — practical,
              optimistic, intellectually curious, and quietly challenging. The audit is written
              that way too.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { t: "Founder First, Business Second", c: "var(--teal)" },
              { t: "Grounded over flashy", c: "var(--yellow-deep)" },
              { t: "Resilient optimism", c: "var(--red)" },
              { t: "Community over solo hero", c: "var(--teal-deep)" },
              { t: "Insight over jargon", c: "var(--yellow-deep)" },
              { t: "Care with accountability", c: "var(--red-deep)" },
            ].map((b) => (
              <div key={b.t} className="card-tech rounded-sm p-6 flex items-start gap-4">
                <span
                  className="mt-1 block w-2.5 h-2.5 rounded-full"
                  style={{ background: b.c }}
                />
                <div className="font-display text-xl tracking-tight leading-tight">{b.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="section-pad py-24 bg-[var(--bg-2)] border-y border-[var(--line)]">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-5">
            <div className="overline mb-3">[05] Run your free audit</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">
              Step one — <span className="text-[var(--teal)]">your LinkedIn audit.</span>
            </h2>
            <p className="mt-6 text-[var(--ink-2)] max-w-md">
              Two minutes of copy-paste, twenty minutes of strategy back. We never scrape — paste
              what matters: headline, About, top experiences and recent posts.
            </p>
            <div className="hairline my-8" />
            <div
              className="rounded-sm p-5 border-l-4"
              style={{ borderColor: "var(--red)", background: "var(--paper)" }}
            >
              <div className="overline mb-2" style={{ color: "var(--red)" }}>
                Step two · members
              </div>
              <p className="font-display text-xl tracking-tight leading-snug">
                Access your personalised Founder Branding Coach inside The Weave.
              </p>
              <p className="text-sm text-[var(--ink-2)] mt-3">
                Mission builder, content pillars, weekly drafts and AI feedback — built on the
                back of your audit. Join after you've read your report.
              </p>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7">
            <AuditForm />
          </div>
        </div>
      </section>
    </div>
  );
}
